import axios from "axios";
import isEmpty from "lodash/isEmpty";
import moment from "moment-timezone";
import { REACT_APP_API_URL, REACT_APP_SITE_KEY } from "../../shared/vars";

import { userCheckLogin, userLogout, userTokenUpdate } from "./user";
import { getCart, clearCart } from "./cart";

// ===================================
//  DELIVERY
// ===================================
export const setDelivery = store => {
	return {
		type: "SET_DELIVERY",
		store
	};
};

export const setDeliverySaving = store => {
	return {
		type: "SET_DELIVERY_SAVING",
		store
	};
};

export const setDeliverySaved = store => {
	return {
		type: "SET_DELIVERY_SAVED",
		store
	};
};

export const setDeliveryLocation = store => {
	return (dispatch, getState) => {
		// Set updating status
		dispatch(setDeliverySaving());

		// Set delivery info
		dispatch(setDelivery(store));

		// Get updated cart totals
		return dispatch(getCart()).then(() => {
			dispatch(setDeliverySaved());
		});
	};
};

// ===================================
//  SHIPPING
// ===================================
export const shippingCheckInProcess = () => {
	return {
		type: "SHIPPING_CHECK_IN_PROCESS"
	};
};

export const shippingCheckError = err => {
	return {
		type: "SHIPPING_CHECK_ERROR",
		err
	};
};

export const shippingCheckSuccess = payload => {
	return {
		type: "SHIPPING_CHECK_SUCCESS",
		payload
	};
};

export function setShipping(data) {
	return (dispatch, getState) => {
		dispatch(shippingCheckInProcess());

		let token = "";
		if (getState().user.customer) token = getState().user.customer.token;
		else if (getState().user.guest) token = getState().user.guest.token;

		let AUTH_HEADER = {
			Authorization: REACT_APP_SITE_KEY + ":Bearer " + token
		};

		return axios
			.post(REACT_APP_API_URL + "customer-shipping-addresses/", data, {
				headers: AUTH_HEADER
			})
			.then(response => {
				// Error explicitly set
				if (!isEmpty(response.data.errors)) {
					// Update user token
					dispatch(userTokenUpdate(!!getState().user.customer ? "customer" : "guest", response.data.token));

					dispatch(shippingCheckError(response.data.errors));
					return Promise.resolve();

					// Error status
				} else if (response.data.status !== 200) {
					// Log user out if token expired, token, etc
					if (response.data.status === 403) {
						dispatch(clearCheckout());
						dispatch(userLogout(!!getState().user.customer ? "customer" : "guest"));
					}

					dispatch(shippingCheckError({ general: response.data.message }));
					return Promise.resolve();

					// Success
				} else {
					// Update user token
					dispatch(userTokenUpdate(!!getState().user.customer ? "customer" : "guest", response.data.token));

					// Update shipping cost with new values
					return dispatch(updateShippingCost(response.data.formatted_values));
				}
			})
			.catch(err => {
				console.log("setShipping error:", err.response.statusText);
				dispatch(shippingCheckError({ general: err.response.statusText }));
				return Promise.resolve();
			});
	};
}

export const updateShippingCost = shippingAddressValues => {
	return (dispatch, getState) => {
		let token = "";
		if (getState().user.customer) token = getState().user.customer.token;
		else if (getState().user.guest) token = getState().user.guest.token;

		let AUTH_HEADER = {
			Authorization: REACT_APP_SITE_KEY + ":Bearer " + token
		};

		const country = shippingAddressValues.country;

		return axios
			.get(REACT_APP_API_URL + "shipping-cost/" + country, {
				headers: AUTH_HEADER
			})
			.then(response => {
				// Error explicitly set
				if (!isEmpty(response.data.errors)) {
					dispatch(shippingCheckError(response.data.errors));
					return Promise.resolve();

					// Error status
				} else if (response.data.status !== 200) {
					// Log user out if token expired, token, etc
					if (response.data.status === 403) {
						dispatch(clearCheckout());
						dispatch(userLogout(!!getState().user.customer ? "customer" : "guest"));
					}

					dispatch(shippingCheckError({ general: response.data.message }));
					return Promise.resolve();

					// Success
				} else {
					shippingAddressValues.shipping_cost = response.data.shipping.cost;

					// Set shipping data
					dispatch(shippingCheckSuccess(shippingAddressValues));

					// Update cart with shipping data
					return dispatch(getCart()).then(() => {
						return Promise.resolve(shippingAddressValues);
					});
				}
			})
			.catch(err => {
				console.log("updateShippingCost error:", err.response.statusText);
				dispatch(shippingCheckError({ general: err.response.statusText }));
				return Promise.resolve();
			});
	};
};

export const setSavedShippingAddress = addressData => {
	return {
		type: "SET_SAVED_SHIPPING_ADDRESS",
		data: addressData
	};
};

// ===================================
//  BILLING
// ===================================
export const billingCheckInProcess = () => {
	return {
		type: "BILLING_CHECK_IN_PROCESS"
	};
};

export const billingCheckError = err => {
	return {
		type: "BILLING_CHECK_ERROR",
		err
	};
};

export const billingCheckSuccess = payload => {
	return {
		type: "BILLING_CHECK_SUCCESS",
		payload
	};
};

export function setBilling(data) {
	return (dispatch, getState) => {
		dispatch(billingCheckInProcess());

		let token = "";
		if (getState().user.customer) token = getState().user.customer.token;
		else if (getState().user.guest) token = getState().user.guest.token;

		let AUTH_HEADER = {
			Authorization: REACT_APP_SITE_KEY + ":Bearer " + token
		};

		return axios
			.post(REACT_APP_API_URL + "customer-billing-addresses/", data, {
				headers: AUTH_HEADER
			})
			.then(response => {
				// Error explicitly set
				if (!isEmpty(response.data.errors)) {
					// Update user token
					dispatch(userTokenUpdate(!!getState().user.customer ? "customer" : "guest", response.data.token));

					dispatch(billingCheckError(response.data.errors));
					return Promise.resolve();

					// Error status
				} else if (response.data.status !== 200) {
					// Log user out if token expired, token, etc
					if (response.data.status === 403 || response.data.status === 405) {
						dispatch(clearCheckout());
						dispatch(userLogout(!!getState().user.customer ? "customer" : "guest"));
					}

					dispatch(billingCheckError({ general: response.data.message }));
					return Promise.resolve();

					// Success
				} else {
					// Update user token
					dispatch(userTokenUpdate(!!getState().user.customer ? "customer" : "guest", response.data.token));

					dispatch(billingCheckSuccess(response.data.formatted_values));
					return Promise.resolve(response.data);
				}
			})
			.catch(err => {
				console.log("setBilling error:", err.response.statusText);
				dispatch(billingCheckError({ general: err.response.statusText }));
				return Promise.resolve();
			});
	};
}

export const setSavedBillingAddress = addressData => {
	return {
		type: "SET_SAVED_BILLING_ADDRESS",
		data: addressData
	};
};

// ===================================
//  ORDER
// ===================================
export const setOrderReviewComplete = () => {
	return {
		type: "SET_ORDER_REVIEW_COMPLETE"
	};
};

// ===================================
//  PAYMENT
// ===================================
export const paymentInProcess = () => {
	return {
		type: "PAYMENT_IN_PROCESS"
	};
};

export const paymentError = err => {
	return {
		type: "PAYMENT_ERROR",
		err
	};
};

export const paymentSuccess = payload => {
	return {
		type: "PAYMENT_SUCCESS",
		payload
	};
};

export function sendPayment(data) {
	return (dispatch, getState) => {
		dispatch(paymentInProcess());

		let token = "";
		if (getState().user.customer) token = getState().user.customer.token;
		else if (getState().user.guest) token = getState().user.guest.token;

		let AUTH_HEADER = {
			Authorization: REACT_APP_SITE_KEY + ":Bearer " + token
		};

		const nums = data.number.split(" ");
		nums.forEach((num, i) => {
			data["card_num_" + (i + 1)] = num;
		});
		if (data.card_num_1 === undefined) data.card_num_1 = "";
		if (data.card_num_2 === undefined) data.card_num_2 = "";
		if (data.card_num_3 === undefined) data.card_num_3 = "";
		if (data.card_num_4 === undefined) data.card_num_4 = "";

		// Put data together to send
		let orderData = data;
		orderData.cart = getState().cart.shoppertrack;
		orderData.shipping = getState().checkout.shipping;
		orderData.billing = getState().checkout.billing;
		orderData.notes = getState().checkout.notes;
		orderData.timezone = moment.tz.guess();

		return axios
			.post(REACT_APP_API_URL + "process-order/", orderData, {
				headers: AUTH_HEADER
			})
			.then(response => {
				console.log(response)
				// Error explicitly set
				if (!isEmpty(response.data.errors)) {
					dispatch(paymentError(response.data.errors));
					return Promise.resolve();

					// Error status
				} else if (response.data.status !== 200) {
					// Log user out if token expired, token, etc
					if (response.data.status === 403) {
						dispatch(clearCheckout());
						dispatch(userLogout(!!getState().user.customer ? "customer" : "guest"));
					}

					dispatch(paymentError({ general: response.data.message }));
					return Promise.resolve();

					// Success
				} else {
					// Empty cart
					dispatch(clearCart());

					// Set payment success
					dispatch(paymentSuccess(response.data));

					// Return a resolved promise with data
					return Promise.resolve(response.data);
				}
			})
			.catch(err => {
				console.log("sendPayment error:", err.response.statusText);
				dispatch(paymentError({ general: err.response.statusText }));
				return Promise.resolve();
			});
	};
}

// ===================================
//  ORDER NOTES
// ===================================
export const setOrderNotes = notes => {
	return {
		type: "SET_ORDER_NOTES",
		notes
	};
};

// ===================================
//  CLEAR
// ===================================
export const clearCheckout = () => {
	return {
		type: "CLEAR_CHECKOUT"
	};
};

// ===================================
//  MISC
// ===================================
export const setActiveCheckoutSectionLoading = name => {
	// console.log('setActiveCheckoutSection:', name)
	return {
		type: "SET_ACTIVE_CHECKOUT_SECTION_LOADING"
	};
};

export const setActiveCheckoutSectionError = err => {
	// console.log('setActiveCheckoutSection:', err)
	return {
		type: "SET_ACTIVE_CHECKOUT_SECTION_ERROR",
		err
	};
};

export const unsetActiveCheckoutSectionError = errorPropertyName => {
	return {
		type: "UNSET_SECTION_ERROR",
		errorPropertyName
	};
};

export const setActiveCheckoutSectionSuccess = name => {
	// console.log('setActiveCheckoutSection:', name)
	return {
		type: "SET_ACTIVE_CHECKOUT_SECTION_SUCCESS",
		name
	};
};

export function setActiveCheckoutSection(name) {
	return (dispatch, getState) => {
		dispatch(setActiveCheckoutSectionLoading());
		dispatch(userCheckLogin("customer")).then(data => {
			dispatch(setActiveCheckoutSectionSuccess(name));
		});
	};
}



// WEBPACK FOOTER //
// ./src/store/actions/checkout.js